// Placement math for the CustomSelect options menu. Kept as a pure function so
// it can be unit-tested without a DOM/layout engine — the component feeds it
// the control and clipping-container rects and renders the result.

export interface PlacementRect {
  top: number
  bottom: number
}

export type MenuDirection = 'top' | 'bottom'

export interface MenuPlacement {
  direction: MenuDirection
  maxHeight: number
}

export interface ComputeMenuPlacementArgs {
  /** Bounding rect of the select control. */
  control: PlacementRect
  /** Bounding rect of the nearest scrollable ancestor (or the viewport). */
  container: PlacementRect
  /** Preferred menu height in px. */
  desiredHeight: number
  /** Gap between control and menu in px. */
  gap?: number
  /** Floor so the menu never collapses to an unusable sliver. */
  minHeight?: number
}

// Decide which way the menu opens and how tall it may be so it stays within the
// container's visible area. Prefer opening downward; flip up only when there
// isn't room below for the desired height and there's more room above. Either
// way, cap the height to the space actually available in the chosen direction.
export function computeMenuPlacement({
  control,
  container,
  desiredHeight,
  gap = 5,
  minHeight = 80,
}: ComputeMenuPlacementArgs): MenuPlacement {
  const spaceBelow = container.bottom - control.bottom - gap
  const spaceAbove = control.top - container.top - gap

  const direction: MenuDirection =
    spaceBelow < desiredHeight && spaceAbove > spaceBelow ? 'top' : 'bottom'

  const available = direction === 'top' ? spaceAbove : spaceBelow
  const maxHeight = Math.max(minHeight, Math.min(desiredHeight, Math.floor(available)))

  return { direction, maxHeight }
}
